import React, { useState, useEffect } from "react";
import { Plus, Search, X, Mail } from "lucide-react";
import { formatDate } from "../utils/mockData";
import { membersApi } from "../utils/api";

const roles = ["President", "Vice President", "Secretary", "Treasurer", "Joint Secretary", "Cultural Head", "Event Manager", "Publicity Head", "Member"];

const roleColors = {
  President: "bg-orange-50 text-orange-800",
  "Vice President": "bg-amber-50 text-amber-800",
  Secretary: "bg-blue-50 text-blue-800",
  Treasurer: "bg-green-50 text-green-800",
  "Joint Secretary": "bg-teal-50 text-teal-800",
  "Cultural Head": "bg-purple-50 text-purple-800",
  "Event Manager": "bg-pink-50 text-pink-800",
  "Publicity Head": "bg-red-50 text-red-800",
  Member: "bg-gray-100 text-gray-700",
};

function MemberModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: "", role: "Member", phone: "", email: "", joinDate: new Date().toISOString().split("T")[0] });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({ ...form, id: `MEM-${Date.now()}`, status: "active" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl z-10">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Add Member</h2>
          <button onClick={onClose}><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Shri / Smt. Name" required
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
              {roles.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="10-digit number"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Join Date</label>
              <input type="date" value={form.joinDate} onChange={(e) => setForm({ ...form, joinDate: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@example.com"
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <button type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors">
            Add Member
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [resending, setResending] = useState(null);

  useEffect(() => {
    membersApi.list()
      .then((res) => setMembers(res.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = members.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.role.toLowerCase().includes(search.toLowerCase()) ||
    (m.phone || "").includes(search) ||
    (m.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (member) => {
    try {
      const saved = await membersApi.create(member);
      setMembers([...members, saved.item || member]);
    } catch {
      setMembers([...members, member]);
    }
    setShowModal(false);
  };

  const handleResendInvite = async (member) => {
    if (!member.email) return;
    setResending(member.id);
    try {
      await membersApi.resendInvite(member.email);
      alert(`Login invite resent to ${member.email}`);
    } catch {
      alert("Failed to resend invite. Check if the email is correct.");
    } finally {
      setResending(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Committee Members</h1>
          <p className="text-sm text-gray-500">{members.filter((m) => m.status === "active").length} active members</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl text-sm shadow-lg shadow-orange-500/20">
          <Plus size={16} /> Add Member
        </button>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, role, phone..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Name</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Role</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Phone</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Email</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Since</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Invite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-xs font-black text-orange-600 flex-shrink-0">
                        {member.name.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold text-gray-800">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${roleColors[member.role] || "bg-gray-100 text-gray-600"}`}>
                      {member.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{member.phone || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate">{member.email || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{member.joinDate ? formatDate(member.joinDate) : "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${member.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {member.status || "active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {member.email && (
                      <button
                        onClick={() => handleResendInvite(member)}
                        disabled={resending === member.id}
                        title="Resend login invite email"
                        className="text-orange-400 hover:text-orange-600 disabled:opacity-40"
                      >
                        <Mail size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile List */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map((member) => (
            <div key={member.id} className="px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center text-sm font-black text-orange-600 flex-shrink-0">
                {member.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{member.name}</p>
                <p className="text-xs text-gray-400">{member.phone || member.email || "No contact"}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${roleColors[member.role] || "bg-gray-100 text-gray-600"}`}>
                {member.role}
              </span>
              {member.email && (
                <button
                  onClick={() => handleResendInvite(member)}
                  disabled={resending === member.id}
                  title="Resend login invite"
                  className="text-orange-400 hover:text-orange-600 disabled:opacity-40 flex-shrink-0"
                >
                  <Mail size={15} />
                </button>
              )}
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">No members found</div>
        )}
      </div>

      {showModal && <MemberModal onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
}
